import { useMemo } from 'react';
import * as THREE from 'three';
import { BOAT_DIMENSIONS, COLORS, MATERIAL_PROPERTIES } from './constants';
import { calculateHullWidthTaper, calculateHullHeightTaper } from './utils';

/**
 * Creates a tapered hull geometry with horizontal and vertical taper at both ends.
 * The geometry is memoized and only created once for performance.
 */
const createHullGeometry = () => {
  const { HULL_LENGTH, HULL_BEAM_WATERLINE, DRAFT, HULL_HEIGHT_ABOVE_WATER, HULL_SEGMENTS } =
    BOAT_DIMENSIONS;

  const halfLength = HULL_LENGTH / 2;
  const halfBeam = HULL_BEAM_WATERLINE / 2;
  const segments = HULL_SEGMENTS;

  const geometry = new THREE.BufferGeometry();
  const positions: number[] = [];
  const indices: number[] = [];

  // Build hull cross-sections along length
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const x = -halfLength + t * HULL_LENGTH;

    // Calculate width and height tapers using utility functions
    const widthScale = calculateHullWidthTaper(t);
    const heightTaper = calculateHullHeightTaper(t);

    const bottomHeight = -DRAFT * heightTaper;
    const topHeight = HULL_HEIGHT_ABOVE_WATER;
    const width = halfBeam * widthScale;

    // Create 6 vertices for this cross-section
    positions.push(x, bottomHeight, -width); // 0: Bottom left
    positions.push(x, bottomHeight, width); // 1: Bottom right
    positions.push(x, topHeight, -width); // 2: Side top left (keeps hard edge)
    positions.push(x, topHeight, width); // 3: Side top right
    positions.push(x, topHeight, -width); // 4: Deck top left (duplicate for sharp edge)
    positions.push(x, topHeight, width); // 5: Deck top right
  }

  // Create faces connecting cross-sections
  for (let i = 0; i < segments; i++) {
    const base = i * 6;
    const next = (i + 1) * 6;

    // Bottom face
    indices.push(base, next, base + 1, base + 1, next, next + 1);
    // Top face
    indices.push(base + 4, base + 5, next + 4, base + 5, next + 5, next + 4);
    // Left side
    indices.push(base, base + 2, next, base + 2, next + 2, next);
    // Right side
    indices.push(base + 1, next + 1, base + 3, base + 3, next + 1, next + 3);
  }

  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
};

/**
 * BoatHull component - renders the tapered racing shell hull
 * Hull is positioned so bottom is at waterline y=0, extending down into water
 */
export const BoatHull = () => {
  const geometry = useMemo(() => createHullGeometry(), []);

  return (
    <mesh geometry={geometry} castShadow receiveShadow>
      <meshStandardMaterial
        color={COLORS.HULL}
        roughness={MATERIAL_PROPERTIES.HULL_ROUGHNESS}
        metalness={MATERIAL_PROPERTIES.HULL_METALNESS}
      />
    </mesh>
  );
};
